import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { InstallCard } from '../../components/InstallCard.js';
import { ApiError, api } from '../../lib/api.js';
import { sendCommand } from '../../lib/commands.js';
import { logout, switchOrganization } from '../../lib/session.js';
import { useSession } from '../../lib/useSession.js';

/**
 * "Meus jogos" — a tela logada.
 *
 * O caminho curto fica em primeiro lugar: "Jogo rápido" cria evento, partida e
 * rodada e já abre o controle. Quem só quer jogar agora não deveria precisar
 * aprender o que é "evento" antes.
 */

interface EventSummary {
  id: string;
  name: string;
  venue: string | null;
  hasOpenGame: boolean;
}

export function Inicio() {
  const session = useSession();
  const navigate = useNavigate();

  const [events, setEvents] = useState<EventSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = session.activeRole === 'OWNER' || session.activeRole === 'ADMIN';
  const hasOrganization = Boolean(session.activeOrganizationId);

  const reload = useCallback(() => {
    if (!hasOrganization) {
      setEvents([]);
      return;
    }

    void api<EventSummary[]>('/api/events')
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [hasOrganization]);

  useEffect(reload, [reload, session.activeOrganizationId]);

  const quickGame = async () => {
    setBusy(true);
    setError(null);

    try {
      const event = await sendCommand<EventSummary>('/api/events', {
        body: { name: 'Bingo rápido' },
      });

      await sendCommand(`/api/events/${event.id}/games`, {
        body: { modality: 'FULL_CARD', freeCenter: true },
      });

      navigate(`/controle?eventId=${event.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível criar o jogo.');
      setBusy(false);
    }
  };

  return (
    <main className="shell">
      <header className="shell-head">
        <div>
          <h1>Meus jogos</h1>
          <p className="hint-text">
            {session.user?.name}
            {session.activeRole ? ` · ${session.activeRole.toLowerCase()}` : ''}
          </p>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => void logout().then(() => navigate('/', { replace: true }))}
        >
          Sair
        </button>
      </header>

      {session.memberships.length > 1 ? (
        <label className="field">
          <span>Organização</span>
          <select
            value={session.activeOrganizationId ?? ''}
            onChange={(event) => void switchOrganization(event.target.value)}
          >
            <option value="" disabled>
              Escolha uma organização
            </option>
            {session.memberships.map((membership) => (
              <option key={membership.organizationId} value={membership.organizationId}>
                {membership.organizationName}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* Superadmin sem membership é um caso real: ele administra a
          plataforma, mas pode não participar de organização nenhuma. A tela
          precisa dizer isso em vez de mostrar uma lista vazia sem explicação. */}
      {!hasOrganization ? (
        <div className="empty-card">
          <p>
            {session.memberships.length > 0
              ? 'Escolha uma organização acima para ver os jogos.'
              : 'Você ainda não participa de nenhuma organização.'}
          </p>
          {session.user?.isPlatformAdmin ? (
            <p className="hint-text">
              Como administrador da plataforma, use <strong>Plataforma</strong> para criar uma
              organização e se adicionar a ela.
            </p>
          ) : (
            <p className="hint-text">Peça a quem administra para adicionar você a uma.</p>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            className="btn primary big block"
            onClick={() => void quickGame()}
            disabled={busy}
          >
            {busy ? 'Preparando…' : '▶ Jogo rápido'}
          </button>
          <p className="hint-text">Começa agora, com cartela cheia e centro livre.</p>

          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}

          <h2>Eventos</h2>

          {events === null ? (
            <p className="hint-text">Carregando…</p>
          ) : events.length === 0 ? (
            <p className="hint-text">Nenhum evento ainda. Use o jogo rápido para começar.</p>
          ) : (
            <div className="event-list">
              {events.map((event) => (
                <div key={event.id} className="event-row">
                  <div>
                    <strong>{event.name}</strong>
                    {event.venue ? <small>{event.venue}</small> : null}
                    {event.hasOpenGame ? (
                      <small className="live">partida em andamento</small>
                    ) : null}
                  </div>
                  <Link className="btn" to={`/controle?eventId=${event.id}`}>
                    Abrir controle
                  </Link>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <InstallCard />

      <nav className="shell-links">
        <Link className="btn" to="/historico">
          Histórico
        </Link>
        <Link className="btn" to="/minha-conta">
          Minha conta
        </Link>
        {isAdmin ? (
          <Link className="btn" to="/admin">
            Administração
          </Link>
        ) : null}
        {session.user?.isPlatformAdmin ? (
          <Link className="btn" to="/admin/plataforma">
            Plataforma
          </Link>
        ) : null}
      </nav>
    </main>
  );
}
